import { type FC, useMemo } from 'react';

import { useSession } from '@eneco/auth';
import { useSelfServiceAccount } from '@eneco/auth-be-self-service';
import RichText from '@eneco/components/RichText/RichText';
import {
  type EnergyTypeDto,
  type GetServiceDeliveryPointOverviewForAddressResponseItem,
  type ContractStatusDto,
  type InvoiceFrequency,
  type InvoiceFrequencyHolder,
  getEnecoBeXapiSiteApiV1AccountsByAccountNumberDeliveryAddressesByAddressIdentifierProducts,
  getEnecoBeXapiSiteApiV1AccountsByAccountNumberDeliveryAddressesByAddressIdentifierServiceDeliveryPoints,
} from '@eneco/dc-be/client';
import useAuthenticatedDCBE from '@eneco/dc-be/hooks/useAuthenticatedDCBE';
import mapScEnumToMap from '@eneco/dc-be/utils/mapScEnumToMap';
import unwrapData from '@eneco/dc-be/utils/unwrapData';
import { Bucket, NotificationBox, TextLink } from '@eneco/sparky';
import { BillingDetails, BillingFrequency } from '@sitecore/types/MyEnecoInvoiceBillingDetails';

import DataErrorNotification from '../../../../../components/DataErrorNotification';
import ReadFormField from '../../../../../components/ReadFormField';
import { ADDRESS_PARAM } from '../../../../../utils/addSearchParams';
import { canUpdateMeterFrequency } from '../utils/canUpdateMeterFrequency';

interface InvoiceFrequencyBucketProps {
  billingDetails: BillingDetails;
  billingFrequency: BillingFrequency;
  addressIdentifier: string | null | undefined;
  fields: { eanLabel?: string };
  isLoadingAddress: boolean;
}

const getEffectiveInvoiceFrequency = (invoiceFrequencyHolder: InvoiceFrequencyHolder) => {
  if (invoiceFrequencyHolder.changeRequestPending) {
    return invoiceFrequencyHolder.requested ?? undefined;
  }
  return invoiceFrequencyHolder.current;
};

const getInvoiceFrequencyDisplayLabel = (
  invoiceFrequencyHolder: InvoiceFrequencyHolder,
  frequencyPeriodMap: Map<InvoiceFrequency | undefined, string>,
) => frequencyPeriodMap.get(getEffectiveInvoiceFrequency(invoiceFrequencyHolder));

const getServiceDeliveryPointLabel = (
  serviceDeliveryPoint: GetServiceDeliveryPointOverviewForAddressResponseItem,
  energyTypeMap: Map<EnergyTypeDto | undefined, string>,
  eanLabel?: string,
) => {
  const energyTypeLabel = energyTypeMap.get(serviceDeliveryPoint.type || 'Electricity');
  return `${energyTypeLabel} - ${eanLabel} ${serviceDeliveryPoint.ean}`;
};

export const InvoiceFrequencyBucket: FC<InvoiceFrequencyBucketProps> = ({
  billingDetails,
  billingFrequency,
  addressIdentifier,
  fields,
  isLoadingAddress,
}) => {
  const { selectedAccount, isCurrentAccountReader } = useSelfServiceAccount();
  const { data: session } = useSession();

  const {
    isLoading: isLoadingServiceDeliveryData,
    data: serviceDeliveryData,
    error: errorServiceDeliveryData,
  } = useAuthenticatedDCBE(
    getEnecoBeXapiSiteApiV1AccountsByAccountNumberDeliveryAddressesByAddressIdentifierServiceDeliveryPoints,
    {
      path: {
        accountNumber: selectedAccount.crmAccountNumber,
        addressIdentifier: addressIdentifier ?? '',
      },
      query: {
        includeMetersFromTerminatedAccounts: true,
      },
    },
    [`/accounts/${selectedAccount.crmAccountNumber}/delivery-addresses/${addressIdentifier}/service-delivery-points`],
    session,
  );

  const {
    isLoading: isLoadingProductsData,
    data: productsData,
    error: errorProductsData,
  } = useAuthenticatedDCBE(
    getEnecoBeXapiSiteApiV1AccountsByAccountNumberDeliveryAddressesByAddressIdentifierProducts,
    {
      path: {
        accountNumber: selectedAccount.crmAccountNumber,
        addressIdentifier: addressIdentifier ?? '',
      },
      query: {
        // https://dev.azure.com/enecomanagedcloud/Eneco%20Belgium/_workitems/edit/880738
        contractStatuses: ['Active', 'TerminationRequested', 'Terminated'] as ContractStatusDto[],
      },
    },
    addressIdentifier
      ? [`/accounts/${selectedAccount.crmAccountNumber}/delivery-addresses/${addressIdentifier}/products`]
      : null,
    session,
  );

  const serviceDeliveryPoints = unwrapData(serviceDeliveryData)?.serviceDeliveryPoints;
  const products = unwrapData(productsData);

  const frequencyPeriodMap = useMemo(
    () => mapScEnumToMap<InvoiceFrequency>(billingFrequency.frequencyPeriodsList.value),
    [billingFrequency.frequencyPeriodsList],
  );

  const energyTypeMap = useMemo(
    () => mapScEnumToMap<EnergyTypeDto>(billingDetails.consumptionTypesList.value),
    [billingDetails.consumptionTypesList],
  );

  const isLoading = isLoadingProductsData || isLoadingServiceDeliveryData || isLoadingAddress;

  const canChangeInvoiceFrequency = serviceDeliveryPoints?.every(serviceDeliveryPoint =>
    canUpdateMeterFrequency(serviceDeliveryPoint),
  );

  const hasActiveProducts = (products?.activeProducts?.length ?? 0) > 0;
  const showUpdateLink = !isCurrentAccountReader && canChangeInvoiceFrequency && hasActiveProducts;
  const showInfoNotification = !canChangeInvoiceFrequency && !isLoading;

  if (serviceDeliveryData?.error?.errors?.status === 404 || productsData?.error?.errors?.status === 404) {
    return null;
  }

  const hasFetchError =
    !isLoading &&
    ((errorServiceDeliveryData !== undefined && !serviceDeliveryPoints) ||
      (errorProductsData !== undefined && !products));

  const updateLinkHref = `${billingFrequency.updateLink?.value.href}?${ADDRESS_PARAM}=${addressIdentifier}`;

  if (hasFetchError) {
    return <DataErrorNotification />;
  }

  const attachServiceDeliveryPoint = <T extends { contractId: string }>(productList: T[]) =>
    productList
      .map(product => ({
        ...product,
        serviceDeliveryPoint: serviceDeliveryPoints?.find(sdp => sdp.contractId === product.contractId),
      }))
      .filter(
        (product): product is T & { serviceDeliveryPoint: GetServiceDeliveryPointOverviewForAddressResponseItem } =>
          product.serviceDeliveryPoint !== undefined,
      );

  const activeProducts = products?.activeProducts ? attachServiceDeliveryPoint(products.activeProducts) : [];
  const terminatedProductsWithoutActiveEan = products?.expiredProducts
    ? attachServiceDeliveryPoint(products.expiredProducts).filter(
        terminatedProduct =>
          !activeProducts.some(
            activeProduct => activeProduct.serviceDeliveryPoint.ean === terminatedProduct.serviceDeliveryPoint.ean,
          ),
      )
    : [];

  const allChangeableProducts = [...activeProducts, ...terminatedProductsWithoutActiveEan];

  return (
    <Bucket title={billingFrequency.title.value}>
      <Bucket.Content>
        {isLoading && <ReadFormField isLoading={isLoading} isLoadingLabel={isLoading} label="" value="" />}
        {allChangeableProducts.map(product => {
          const { serviceDeliveryPoint } = product;
          const label = getServiceDeliveryPointLabel(serviceDeliveryPoint, energyTypeMap, fields.eanLabel);
          const value = getInvoiceFrequencyDisplayLabel(serviceDeliveryPoint.invoiceFrequency, frequencyPeriodMap);

          return <ReadFormField key={product.contractId} label={label} value={value} />;
        })}
        {showUpdateLink && (
          <TextLink emphasis="high" href={updateLinkHref}>
            {billingFrequency.updateLink?.value.text}
          </TextLink>
        )}
        {showInfoNotification && (
          <NotificationBox
            isAlert={false}
            title={billingFrequency.infoNotification.value.title}
            text={<RichText html={billingFrequency.infoNotification.value.content} />}
            variant={billingFrequency.infoNotification.value.variant ?? 'info'}
          />
        )}
      </Bucket.Content>
    </Bucket>
  );
};
